<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { NavMenuDto } from '@s-blog/shared-types';
import { publicApi } from '@/api/public';
import { useLang } from '@/composables/useLang';
import { useSiteConfig } from '@/composables/useSiteConfig';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import Glass from './Glass.vue';
import SearchOverlay from './SearchOverlay.vue';

const route = useRoute();
const router = useRouter();
const { lang, isZh, toggle } = useLang();
const { config } = useSiteConfig();
const { features } = useFeatureFlags();

// 只用模板真正读到的 5 个字段；不假装是完整 NavMenuDto，避免造假 createdAt / updatedAt
type NavItem = Pick<NavMenuDto, 'id' | 'label' | 'labelEn' | 'routePath' | 'external'>;

const FALLBACK_NAV: NavItem[] = [
  { id: 'home', label: '首页', labelEn: 'Home', routePath: '/', external: false },
  { id: 'blog', label: '博客', labelEn: 'Blog', routePath: '/blog', external: false },
  { id: 'now', label: '在做', labelEn: 'Now', routePath: '/now', external: false },
  { id: 'notes', label: '想法', labelEn: 'Notes', routePath: '/notes', external: false },
  { id: 'work', label: '作品', labelEn: 'Work', routePath: '/works', external: false },
  { id: 'about', label: '关于', labelEn: 'About', routePath: '/about', external: false },
];

const nav = ref<NavItem[]>(FALLBACK_NAV);
const drawerOpen = ref(false);
const searchOpen = ref(false);

onMounted(async () => {
  // 首次进站带 ?q=xxx 直接开搜索 deep link (搜索关掉时忽略)
  if (route.query.q && features.value.search) searchOpen.value = true;
  try {
    const remote = await publicApi.navMenus();
    if (remote.length > 0) nav.value = remote;
  } catch {
    // 网络错误就用 fallback
  }
  window.addEventListener('keydown', onKeydown);
});

// 路由 query 变化（用户粘贴带 ?q= 的链接 / 后退到带 q 的历史）也要开搜索
watch(
  () => route.query.q,
  (q) => {
    if (q && !searchOpen.value && features.value.search) searchOpen.value = true;
  },
);

onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));

// Cmd/Ctrl+K 全站搜索快捷键
function onKeydown(e: KeyboardEvent) {
  if (!features.value.search) return; // 站长关了搜索
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    searchOpen.value = true;
  }
}

// 路由切走自动关抽屉
watch(
  () => route.fullPath,
  () => {
    drawerOpen.value = false;
  },
);

// 抽屉打开时锁滚动
watch(drawerOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : '';
});

const brand = computed(() => config.value?.siteName ?? 's-blog');

function isActive(item: NavItem) {
  if (item.routePath === '/') return route.path === '/';
  return route.path.startsWith(item.routePath);
}

function goHome() {
  router.push('/');
}

function navigate(item: NavItem, e: MouseEvent) {
  if (item.external) return; // 走原生 <a>
  e.preventDefault();
  router.push(item.routePath);
  drawerOpen.value = false;
}

function openSearch() {
  searchOpen.value = true;
  drawerOpen.value = false;
}
</script>

<template>
  <nav class="topnav" :aria-label="isZh ? '主导航' : 'Primary'">
    <!-- 桌面态：完整 nav pill -->
    <div class="topnav__pill topnav__pill--desktop">
      <Glass
        :radius="100"
        :blur="24"
        tint="var(--aurora-glass-tint-strong)"
        border="var(--aurora-glass-border-strong)"
      >
        <div class="topnav__inner">
          <button class="topnav__brand" @click="goHome">
            <span class="topnav__brand-text">{{ brand }}</span>
            <span class="topnav__brand-dot">.</span>
          </button>
          <span class="topnav__divider" />
          <template v-for="it in nav" :key="it.id">
            <a
              v-if="it.external"
              :href="it.routePath"
              target="_blank"
              rel="noopener"
              class="topnav__item"
            >
              {{ isZh ? it.label : it.labelEn ?? it.label }}
            </a>
            <button
              v-else
              class="topnav__item"
              :class="{ 'topnav__item--active': isActive(it) }"
              @click="navigate(it, $event)"
            >
              {{ isZh ? it.label : it.labelEn ?? it.label }}
            </button>
          </template>
        </div>
      </Glass>
    </div>

    <!-- 移动态:品牌 + 汉堡 -->
    <div class="topnav__pill topnav__pill--mobile">
      <Glass
        :radius="100"
        :blur="24"
        tint="var(--aurora-glass-tint-strong)"
        border="var(--aurora-glass-border-strong)"
      >
        <div class="topnav__inner topnav__inner--mobile">
          <button class="topnav__brand" @click="goHome">
            <span class="topnav__brand-text">{{ brand }}</span>
            <span class="topnav__brand-dot">.</span>
          </button>
          <button
            v-if="features.search"
            class="topnav__icon-btn"
            :aria-label="isZh ? '搜索' : 'search'"
            :aria-expanded="searchOpen"
            aria-controls="search-overlay"
            @click="openSearch"
          >
            ⌕
          </button>
          <button
            class="topnav__icon-btn topnav__hamburger"
            :class="{ 'topnav__hamburger--open': drawerOpen }"
            :aria-label="isZh ? '菜单' : 'menu'"
            :aria-expanded="drawerOpen"
            @click="drawerOpen = !drawerOpen"
          >
            <span /><span /><span />
          </button>
        </div>
      </Glass>
    </div>

    <!-- 桌面态:搜索 + 语言 -->
    <div class="topnav__pill topnav__pill--desktop">
      <Glass
        :radius="100"
        :blur="24"
        tint="var(--aurora-glass-tint-strong)"
        border="var(--aurora-glass-border-strong)"
      >
        <div class="topnav__inner">
          <button
            v-if="features.search"
            class="topnav__icon-btn"
            :title="isZh ? '搜索 (⌘K)' : 'Search (⌘K)'"
            :aria-label="isZh ? '搜索' : 'search'"
            :aria-expanded="searchOpen"
            aria-controls="search-overlay"
            @click="openSearch"
          >
            ⌕
          </button>
          <span v-if="features.search" class="topnav__divider" />
          <button class="topnav__lang" @click="toggle">{{ lang === 'zh' ? 'EN' : '中' }}</button>
        </div>
      </Glass>
    </div>

    <!-- 移动端 drawer：Teleport 到 body 脱离 .topnav 的 stacking context -->
    <Teleport to="body">
      <Transition name="drawer">
        <div v-if="drawerOpen" class="topnav__drawer" @click.self="drawerOpen = false">
          <div class="topnav__drawer-panel">
            <div class="topnav__drawer-list">
              <template v-for="it in nav" :key="it.id">
                <a
                  v-if="it.external"
                  :href="it.routePath"
                  target="_blank"
                  rel="noopener"
                  class="topnav__drawer-item"
                  @click="drawerOpen = false"
                >
                  {{ isZh ? it.label : it.labelEn ?? it.label }}
                </a>
                <button
                  v-else
                  class="topnav__drawer-item"
                  :class="{ 'topnav__drawer-item--active': isActive(it) }"
                  @click="navigate(it, $event)"
                >
                  {{ isZh ? it.label : it.labelEn ?? it.label }}
                </button>
              </template>
            </div>
            <button class="topnav__drawer-lang" @click="toggle">
              {{ lang === 'zh' ? 'English' : '中文' }}
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 全局搜索覆盖层 -->
    <SearchOverlay :open="searchOpen" @close="searchOpen = false" />
  </nav>
</template>

<style scoped>
.topnav {
  position: sticky;
  top: 16px;
  z-index: 50;
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  margin-top: 16px;
  pointer-events: none;
}
.topnav__pill {
  pointer-events: auto;
}
.topnav__pill :deep(.glass) {
  padding: 6px;
}
.topnav__inner {
  display: flex;
  gap: 2px;
  align-items: center;
}
.topnav__inner--mobile {
  gap: 4px;
}
.topnav__brand {
  border: none;
  background: transparent;
  color: var(--aurora-text);
  font-family: var(--font-display);
  font-size: 16px;
  font-style: italic;
  font-weight: 400;
  padding: 6px 14px 6px 10px;
  cursor: pointer;
  letter-spacing: -0.3px;
}
.topnav__brand-text {
  background-image: linear-gradient(135deg, var(--aurora-accent), var(--aurora-magenta));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: var(--aurora-accent);
}
.topnav__brand-dot {
  color: var(--aurora-accent);
}
.topnav__divider {
  width: 1px;
  height: 16px;
  background: rgba(255, 255, 255, 0.15);
  margin: 0 4px;
}
.topnav__item {
  padding: 8px 14px;
  border-radius: 100px;
  border: none;
  background: transparent;
  color: rgba(255, 245, 232, 0.72);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.25s cubic-bezier(0.2, 0.7, 0.3, 1);
}
.topnav__item:hover {
  color: var(--aurora-text);
}
.topnav__item--active {
  background: linear-gradient(135deg, var(--aurora-accent), var(--aurora-magenta));
  color: #fff;
  box-shadow: 0 4px 14px rgba(255, 126, 95, 0.35);
}
.topnav__lang {
  padding: 8px 12px;
  border-radius: 100px;
  border: none;
  background: transparent;
  color: rgba(255, 245, 232, 0.8);
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  letter-spacing: 1px;
}

/* 移动态 */
.topnav__pill--mobile {
  display: none;
}
.topnav__icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 100px;
  border: none;
  background: transparent;
  color: var(--aurora-text-soft);
  font-size: 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.topnav__icon-btn:hover {
  color: var(--aurora-text);
  background: rgba(255, 255, 255, 0.06);
}
.topnav__hamburger {
  position: relative;
}
.topnav__hamburger span {
  position: absolute;
  left: 9px;
  right: 9px;
  height: 1.5px;
  background: currentColor;
  border-radius: 2px;
  transition: all 0.25s cubic-bezier(0.2, 0.7, 0.3, 1);
}
.topnav__hamburger span:nth-child(1) {
  top: 12px;
}
.topnav__hamburger span:nth-child(2) {
  top: 17px;
}
.topnav__hamburger span:nth-child(3) {
  top: 22px;
}
.topnav__hamburger--open span:nth-child(1) {
  top: 17px;
  transform: rotate(45deg);
}
.topnav__hamburger--open span:nth-child(2) {
  opacity: 0;
}
.topnav__hamburger--open span:nth-child(3) {
  top: 17px;
  transform: rotate(-45deg);
}

.topnav__drawer {
  position: fixed;
  inset: 0;
  z-index: 49;
  background: rgba(20, 8, 24, 0.72);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 80px 16px 24px;
}
.topnav__drawer-panel {
  width: min(360px, 100%);
  background: rgba(40, 20, 40, 0.85);
  border: 1px solid var(--aurora-glass-border-strong);
  border-radius: 22px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.topnav__drawer-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.topnav__drawer-item {
  background: transparent;
  border: none;
  text-decoration: none;
  text-align: left;
  font-family: var(--font-display);
  font-size: 18px;
  color: var(--aurora-text-soft);
  padding: 12px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.topnav__drawer-item:hover {
  background: rgba(255, 255, 255, 0.05);
  color: var(--aurora-text);
}
.topnav__drawer-item--active {
  background: linear-gradient(135deg, var(--aurora-accent), var(--aurora-magenta));
  color: #fff;
}
.topnav__drawer-lang {
  align-self: flex-start;
  background: transparent;
  border: 1px solid rgba(255, 220, 200, 0.18);
  color: var(--aurora-text-soft);
  border-radius: 100px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 1px;
  padding: 8px 16px;
  cursor: pointer;
}

@media (max-width: 768px) {
  .topnav__pill--desktop {
    display: none;
  }
  .topnav__pill--mobile {
    display: block;
  }
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.2s ease;
}
.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}
</style>
